import { useState, useCallback } from 'react';
import { useNotification } from '../../../../contexts/NotificationContext';

export const USER_FORM_INITIAL_STATE = {
  username: '',
  fullname: '',
  email: '',
  password: '',
  confirmPassword: '',
  role: '',
};

const INITIAL_ROLES = ['Admin', 'Doctor', 'Nurse', 'Staff', 'OT Manager'];
const EMAIL_REGEX = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
const NAME_REGEX = /^[a-zA-Z\s.-]*$/;

const validatePassword = (password) => {
  if (password.length < 8) return 'Password must be at least 8 characters';
  if (!/[A-Z]/.test(password)) return 'Password must contain at least one uppercase letter';
  if (!/[a-z]/.test(password)) return 'Password must contain at least one lowercase letter';
  if (!/[0-9]/.test(password)) return 'Password must contain at least one number';
  return '';
};

export const useUserForm = (initialData = USER_FORM_INITIAL_STATE, isEdit = false) => {
  const { showNotification } = useNotification();
  const [formData, setFormData] = useState(initialData);
  const [errors, setErrors] = useState({ username: '', fullname: '', email: '', password: '', confirmPassword: '', role: '' });

  const setInitialData = useCallback((dataOrUpdater) => setFormData(dataOrUpdater), []);

  // Only allows letters, spaces, dots and dashes for name fields
  const handleNameChange = (e) => {
    const { name, value } = e.target;
    if (value === '' || NAME_REGEX.test(value)) {
      setFormData(prev => ({ ...prev, [name]: value }));
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setErrors(prev => ({ ...prev, [name]: '' }));

    if (name === 'password') {
      const err = value ? validatePassword(value) : '';
      setErrors(prev => ({ ...prev, password: err }));
    }
    if (name === 'confirmPassword') {
      setErrors(prev => ({ ...prev, confirmPassword: value !== formData.password ? 'Passwords do not match' : '' }));
    }
    if (name === 'email') {
      setErrors(prev => ({ ...prev, email: value && !EMAIL_REGEX.test(value) ? 'Invalid email format' : '' }));
    }
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    let error = '';
    switch (name) {
      case 'username': if (!value) error = 'Username is required'; break;
      case 'fullname': if (!value) error = 'Full name is required'; break;
      case 'email':
        if (!value) error = 'Email is required';
        else if (!EMAIL_REGEX.test(value)) error = 'Invalid email format';
        break;
      case 'role': if (!value) error = 'Role is required'; break;
      case 'password':
        if (!isEdit && !value) error = 'Password is required';
        else if (value) error = validatePassword(value);
        break;
      case 'confirmPassword':
        if (formData.password && !value) error = 'Please confirm password';
        else if (value !== formData.password) error = 'Passwords do not match';
        break;
      default: break;
    }
    if (error) setErrors(prev => ({ ...prev, [name]: error }));
  };

  const validate = () => {
    const newErrors = {
      username: !formData.username ? 'Username is required' : '',
      fullname: !formData.fullname ? 'Full name is required' : '',
      email: !formData.email ? 'Email is required' : !EMAIL_REGEX.test(formData.email) ? 'Invalid email format' : '',
      // For Add: password required; for Edit: only validate if filled in
      password: !isEdit
        ? (!formData.password ? 'Password is required' : validatePassword(formData.password))
        : (formData.password ? validatePassword(formData.password) : ''),
      confirmPassword: !isEdit
        ? (!formData.confirmPassword ? 'Please confirm password' : formData.password !== formData.confirmPassword ? 'Passwords do not match' : '')
        : (formData.password && !formData.confirmPassword ? 'Please confirm password' : formData.password !== formData.confirmPassword ? 'Passwords do not match' : ''),
      role: !formData.role ? 'Role is required' : '',
    };
    setErrors(newErrors);
    const hasError = Object.values(newErrors).some(e => e);
    if (hasError) {
      const first = Object.values(newErrors).find(e => e);
      showNotification('error', `Required Field: ${first}`);
    }
    return !hasError;
  };

  const buildAddPayload = () => ({
    username: formData.username,
    password: formData.password,
    confirm_password: formData.confirmPassword,
    email: formData.email,
    fullname: formData.fullname,
    role: formData.role,
  });

  const buildEditPayload = () => {
    const payload = {
      username: formData.username,
      email: formData.email,
      fullname: formData.fullname,
      role: formData.role,
    };
    if (formData.password) {
      payload.password = formData.password;
      payload.confirm_password = formData.confirmPassword;
    }
    return payload;
  };

  return {
    formData,
    errors,
    setInitialData,
    handleNameChange,
    handleChange,
    handleBlur,
    validate,
    buildAddPayload,
    buildEditPayload,
    INITIAL_ROLES,
  };
};
